// Client-only desktop task notifications.
// Wraps the browser Notification API + a localStorage preference.
// No React, no service worker, no server. Fires only while a tab is open.

const PREF_KEY = 'desktopNotifications';
const CLICK_ROUTE = '/app/task-list';

const TASK_LABELS = {
  'detect-ai': 'AI Detection',
  'plagiarism': 'Plagiarism check',
  'humanize': 'AI Humanization',
};

const submitListeners = new Set();

export const isSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const getPermission = () =>
  isSupported() ? window.Notification.permission : 'denied';

export const getPreference = () => {
  try {
    return localStorage.getItem(PREF_KEY);
  } catch (e) {
    return null;
  }
};

export const setPreference = (value) => {
  try {
    localStorage.setItem(PREF_KEY, value);
  } catch (e) {
    /* localStorage unavailable; ignore */
  }
};

export const isEffectivelyEnabled = () =>
  getPreference() === 'on' && getPermission() === 'granted';

export const requestPermission = async () => {
  if (!isSupported()) return 'denied';
  try {
    return await window.Notification.requestPermission();
  } catch (e) {
    return getPermission();
  }
};

export const enable = async () => {
  setPreference('on');
  if (getPermission() === 'default') {
    await requestPermission();
  }
  return isEffectivelyEnabled();
};

export const disable = () => setPreference('off');

export const shouldPrompt = () =>
  isSupported() && getPreference() === null && getPermission() === 'default';

// --- contextual prompt pub/sub ---
export const onTaskSubmitted = (cb) => {
  submitListeners.add(cb);
  return () => submitListeners.delete(cb);
};

export const signalTaskSubmitted = () => {
  submitListeners.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      /* a listener error must not break submission */
    }
  });
};

// Returns { title, body } or null if this status should not notify.
const buildMessage = (label, status) => {
  if (status === 'completed') {
    const bodies = {
      'AI Detection': 'Your AI Detection result is ready to view.',
      'Plagiarism check': 'Your plagiarism check is ready to view.',
      'AI Humanization': 'Your humanized content is ready to view.',
    };
    return { title: `Your ${label} is ready`, body: bodies[label] };
  }
  if (status === 'insufficient-credits') {
    return {
      title: 'You ran out of credits',
      body: `Your ${label} could not be completed. Add credits to finish it.`,
    };
  }
  if (status === 'failed' || status === 'error' || status === 'failed-concurrent-task') {
    return {
      title: `Your ${label} couldn't be completed`,
      body: 'Something went wrong. Please try again.',
    };
  }
  return null; // content-filtered and anything else: handled inline by the UI
};

export const notifyTask = (task) => {
  try {
    if (!task || !isEffectivelyEnabled()) return;
    // If the user is actively looking at the app, they already see the result.
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      typeof document.hasFocus === 'function' &&
      document.hasFocus()
    ) {
      return;
    }
    const type = Array.isArray(task.taskType) ? task.taskType[0] : task.taskType;
    const label = TASK_LABELS[type];
    if (!label) return; // only emailed task types

    const msg = buildMessage(label, task.contentStatus);
    if (!msg) return;

    const notification = new window.Notification(msg.title, {
      body: msg.body,
      tag: task.id || task._id,
      icon: '/favicon.ico',
    });
    notification.onclick = () => {
      try {
        window.focus();
      } catch (e) {
        /* ignore */
      }
      window.location.href = CLICK_ROUTE;
      notification.close();
    };
  } catch (e) {
    /* never let a notification failure break task resolution */
  }
};
